import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentSnapshot } from '@angular/fire/firestore';
import { User } from '../interfaces/user';
import { AngularFireAuth } from '@angular/fire/auth';
import { Subject, Subscription } from 'rxjs';
import firebase from 'firebase/app';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  user?: User;
  userSubscription?: Subscription;
  userSubject = new Subject();
  authModalVisible = false;

  constructor(
    private firestore: AngularFirestore,
    private auth: AngularFireAuth
  ) {}

  listenToAuthChange(): void {
    // listen to auth state change and update user
    this.auth.onAuthStateChanged((authUser: firebase.User | null) => {
      if (authUser) {
        this.getUser(authUser?.uid).then(
          (userDocSnapshot: DocumentSnapshot<User>) => {
            if (userDocSnapshot.exists) {
              // if user exists, subscribe to it
              this.subscribeToUser(userDocSnapshot.data()!.uid);
            } else {
              // if not, set a new user and then subscribe
              this.createUser(authUser).then(() =>
                this.subscribeToUser(authUser.uid)
              );
            }
          }
        );
      } else {
        this.user = null;
      }
    });
  }

  // search user database for authUser uid
  getUser(userUid: string | undefined): Promise<DocumentSnapshot<User>> {
    return this.firestore
      .collection<User>('users')
      .doc<any>(userUid)
      .get()
      .toPromise();
  }

  // set a new user
  createUser(authUser: firebase.User): Promise<void> {
    return new Promise((resolve) => {
      // model authUser
      const userData: User = {
        name: authUser.displayName!,
        image: authUser.photoURL!,
        email: authUser.email!,
        uid: authUser.uid!,
        colorTheme: 'dark',
      };
      // then push it to firestore
      this.firestore
        .collection<User>('users')
        .doc(authUser.uid)
        .set(userData)
        .then(() => {
          resolve();
        });
    });
  }

  // update an existing user
  updateUser(userId: string, partialUser: Partial<User>): Promise<void> {
    return this.firestore
      .collection<User>('users')
      .doc(userId)
      .update(partialUser);
  }

  // subscribe to user document
  subscribeToUser(userUid: string): void {
    // subscribe to new user
    this.userSubscription = this.firestore
      .collection<User>('users')
      .doc(userUid)
      .valueChanges()
      .subscribe((user: User | undefined) => {
        this.user = user;
        this.userSubject.next();
      });
  }

  // sign user out
  signOut(): void {
    this.auth.signOut();
    this.userSubscription.unsubscribe();
  }

  // triggers firebase auth
  signIn(
    provider: string,
    credentials?: { email: string; password: string }
  ): Promise<void> {
    let signIn: Promise<any>;
    // if provider is specified, use it
    if (!credentials) {
      const providers = {
        google: new firebase.auth.GoogleAuthProvider(),
        facebook: new firebase.auth.FacebookAuthProvider(),
      };
      signIn = this.auth.signInWithPopup(providers[provider]);
    }
    // if no provider is specified, use email and password
    else {
      signIn = this.auth.signInWithEmailAndPassword(
        credentials.email,
        credentials.password
      );
    }
    return new Promise((resolve) => {
      signIn.then(() => resolve());
    });
  }

  // triggers firebase user registration
  register(email: string, password: string, name: string): Promise<string> {
    return new Promise((resolve) => {
      this.auth
        .createUserWithEmailAndPassword(email, password)
        .then((credential: firebase.auth.UserCredential) => {
          const user = credential.user;
          // return uid of registered user
          resolve(user.uid);
        });
    });
  }
}
